import React from "react";
import {GlobalContext} from "../../GlobalContext";
import {Accordion, Card, Col, Row} from "react-bootstrap";
import {withRouter} from 'react-router-dom';
import {SpinnerWrapper} from "../common/SpinnerWrapper";
import LienCaseView from "./LienCaseView";
import WcDataSheetView from "./WcDataSheetView";
import {Glyph} from "../common/Glyph";
import {nowMoment, TIMEZONE} from "../../utils/Dates";
import moment from "moment-timezone";

function wcReferralMoment(sheet) {
    if (!sheet?.referralDate) {
        return null;
    }
    const m = moment(sheet.referralDate);
    return m.isValid() ? m : null;
}

function sortWcDataSheets(sheets) {
    return [...sheets].sort((a, b) => {
        const at = wcReferralMoment(a)?.valueOf() || 0;
        const bt = wcReferralMoment(b)?.valueOf() || 0;
        return bt - at;
    });
}

function formatWcReferralDateTime(referralDate) {
    if (!referralDate) {
        return '';
    }
    const m = moment(referralDate);
    return m.isValid() ? m.tz(TIMEZONE).format('MM/DD/YYYY h:mm A') : referralDate;
}

class PatientLienList extends React.Component {
    static contextType = GlobalContext;

    constructor(props) {
        super(props);
        this.state = {
            patient: null,
            lienCases: [],
            wcDataSheets: [],
            loading: true,
            lienAccordionKey: null,
            wcAccordionKey: null
        }
    }

    componentDidUpdate(prevProps, prevState, snapshot) {
        let patientId = this.props.match.params.patientId;
        let prevPatientId = prevProps.match.params.patientId;
        if (!this.state.loading && prevPatientId !== patientId) {
            this.loadPatient();
        }
    }

    componentDidMount() {
        this.loadPatient();
    }

    getLienId = () => {
        let params = new URL(document.location.toString()).searchParams;
        let id = params.get("lienId");
        if (id) return parseInt(id);
        return null;
    }

    getWcReferralId = () => {
        let params = new URL(document.location.toString()).searchParams;
        let id = params.get("wcReferralId");
        if (id) return parseInt(id);
        return null;
    }

    loadPatient = () => {
        let patientId = this.props.match.params.patientId;
        if (patientId > 0) {
            this.setState({
                patient: null,
                lienCases: [],
                wcDataSheets: [],
                loading: true,
                lienAccordionKey: null,
                wcAccordionKey: null
            }, () => {
                const patientReq = this.context.apiRequest("GET", `/patients/${patientId}`);
                const lienReq = this.context.apiRequest("GET", `/patients/${patientId}/lienCases`);
                const wcReq = this.context.apiRequest("GET", `/patients/${patientId}/wc-datasheets`);

                Promise.all([patientReq, lienReq, wcReq])
                    .then(([patientResp, lienResp, wcResp]) => {
                        const patient = patientResp?.data?.patientId ? patientResp.data : null;
                        const lienCases = lienResp?.data || [];
                        const wcDataSheets = sortWcDataSheets(wcResp?.data || []);
                        const lienId = this.getLienId();
                        const wcReferralId = this.getWcReferralId();

                        this.setState({
                            patient,
                            lienCases,
                            wcDataSheets,
                            lienAccordionKey: lienId
                                ? parseInt(lienId)
                                : (lienCases.length > 0 ? lienCases[0].id : null),
                            wcAccordionKey: wcReferralId || (wcDataSheets.length > 0 ? wcDataSheets[0].referralId : null),
                            loading: false
                        });
                    })
                    .catch(() => this.setState({loading: false}));
            });
        } else {
            this.setState({
                patient: null,
                lienCases: [],
                wcDataSheets: [],
                loading: true,
                lienAccordionKey: null,
                wcAccordionKey: null
            });
        }
    };

    newLien = () => {
        return {
            id: -1,
            patientId: this.state.patient.patientId,
            dateOfInjury: nowMoment().format('M/D/YY'),
            attorneyId: null,
            attorneyName: '(Unspecified Attorney)',
            attorneyGroupId: null,
            created: null,
            firstApptDateTime: null,
            paidDate: '',
            status: 'PENDING',
            paidTo: '',
            externalCaseNumber: '',
            flagged: 0,
            billType: '',
            caseManagerName: ''
        }
    }

    addLienCase = () => {
        let cases = this.state.lienCases;
        let lien = this.newLien();
        cases.unshift(lien);
        this.setState({lienCases: cases, lienAccordionKey: lien.id});
    }

    lienAccordionSelect = (e) => {
        this.setState({lienAccordionKey: e});
    }

    wcAccordionSelect = (e) => {
        this.setState({wcAccordionKey: e});
    }

    setCase = (index, lc) => {
        let cases = [...this.state.lienCases];
        cases[index] = lc;
        this.setState({lienCases: cases, lienAccordionKey: lc.id});
    }

    setWcSheet = (index, sheet) => {
        let sheets = [...this.state.wcDataSheets];
        sheets[index] = sheet;
        this.setState({wcDataSheets: sortWcDataSheets(sheets), wcAccordionKey: sheet.referralId});
    }

    cancelEdit = (index) => {
        let cases = this.state.lienCases;
        if (cases[index].id === -1) {
            cases.splice(index, 1);
            this.setState({lienAccordionKey: cases.length > 0 ? cases[0].id : null});
        } else {
            this.context.apiRequest("GET", `/liens/${cases[index].id}`)
                .then(resp => {
                    if (resp.data) {
                        const updated = [...this.state.lienCases];
                        updated[index] = resp.data;
                        this.setState({lienCases: updated});
                    }
                });
        }
    }

    cancelWcEdit = (index) => {
        const referralId = this.state.wcDataSheets[index].referralId;
        this.context.apiRequest("GET", `/referrals/${referralId}/wc-datasheet`)
            .then(resp => {
                if (resp.data) {
                    const updated = [...this.state.wcDataSheets];
                    updated[index] = {
                        ...updated[index],
                        ...resp.data,
                        referralId
                    };
                    this.setState({wcDataSheets: updated});
                }
            });
    }

    wcHeaderLabel(sheet) {
        const doi = sheet.dateOfInjury || '[No DOI]';
        const tpa = sheet.tpaName || '[No TPA Specified]';
        return `${doi} - ${tpa}`;
    }

    wcReferralTimestamp(sheet) {
        return formatWcReferralDateTime(sheet.referralDate);
    }

    renderLienSection(p) {
        const cases = this.state.lienCases;
        return <>
            <Row className="align-items-center mb-2">
                <Col xs={10}><h5 className="mb-0">Lien Cases</h5></Col>
                <Col xs={2} className="text-right">
                    <Glyph name="plus-circle" className="clickable" onClick={this.addLienCase}/>
                </Col>
            </Row>
            {cases && cases.length > 0 ?
                <Accordion onSelect={this.lienAccordionSelect} activeKey={this.state.lienAccordionKey}>
                    {cases.map((lc, i) =>
                        <Card key={lc.id}>
                            <Accordion.Toggle as={Card.Header} eventKey={lc.id} className="clickable">
                                <Row>{`${lc.dateOfInjury || '[No DOI]'} - ${lc.linkedAttorneyName || lc.attorneyName || '[No Attorney Specified]'}`}</Row>
                            </Accordion.Toggle>
                            <Accordion.Collapse eventKey={lc.id}>
                                <Card.Body>
                                    <LienCaseView lienCase={lc} patient={p} onSave={(c => this.setCase(i, c))}
                                                  onCancel={() => this.cancelEdit(i)} onDelete={this.loadPatient}/>
                                </Card.Body>
                            </Accordion.Collapse>
                        </Card>)}
                </Accordion>
                : <Row><Col>No liens for this patient.</Col></Row>
            }
        </>;
    }

    renderWcSection() {
        const sheets = this.state.wcDataSheets;
        return <>
            <h5 className="mb-2 mt-4">Workers Comp</h5>
            {sheets && sheets.length > 0 ?
                <Accordion onSelect={this.wcAccordionSelect} activeKey={this.state.wcAccordionKey}>
                    {sheets.map((sheet, i) => {
                        const referralTimestamp = this.wcReferralTimestamp(sheet);
                        return <Card key={sheet.referralId}>
                            <Accordion.Toggle as={Card.Header} eventKey={sheet.referralId} className="clickable">
                                <Row>
                                    <Col>{this.wcHeaderLabel(sheet)}</Col>
                                    {referralTimestamp &&
                                        <Col xs="auto" className="text-muted small">{referralTimestamp}</Col>}
                                </Row>
                            </Accordion.Toggle>
                            <Accordion.Collapse eventKey={sheet.referralId}>
                                <Card.Body>
                                    <WcDataSheetView wcDataSheet={sheet} onSave={(s => this.setWcSheet(i, s))}
                                                     onCancel={() => this.cancelWcEdit(i)}/>
                                </Card.Body>
                            </Accordion.Collapse>
                        </Card>;
                    })}
                </Accordion>
                : <Row><Col>No workers comp data sheets for this patient.</Col></Row>
            }
        </>;
    }

    render() {
        let p = this.state.patient;

        return <Card>
            <Card.Body>
                <Card.Title>
                    <h4>{p ? `Patient: ${p.firstName} ${p.lastName} ${p.dob || ''}` : `Loading...`}</h4>
                </Card.Title>
                <SpinnerWrapper visible={this.state.loading}>
                    {this.renderLienSection(p)}
                    {this.renderWcSection()}
                </SpinnerWrapper>
            </Card.Body>
        </Card>;
    }
}

export default withRouter(PatientLienList);
